import { PropsWithChildren, ReactNode, useCallback } from 'react'
import { DropdownTrigger } from '../DropdownTrigger/DropdownTrigger'
import { Button } from '../Button'
import { EmojiPicker } from './EmojiPicker'
import Styles from './AlmanacFileTitle.module.scss'
import { ErrorBoundary } from '@sentry/react'
import { AssetType, BaseEmoji, ScheduledPostTarget, BrandPlan } from 'types'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { externalFileEmoji } from 'components/ReviewsWizard/FileSelection/urlValidationUtils'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { observer } from 'mobx-react-lite'
import { ButtonProps } from 'components/Button/Types'
import { Design, Document } from 'blaze-ui/icons'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useHeaderTheme } from 'hooks/useHeaderTheme'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { channelTypeFromProvider } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { TYPES_WITH_PREVIEW } from 'screens/Calendar/FullScreenPreview/Types'

interface Props extends Pick<ButtonProps, 'size'> {
  title: string
  fileKey?: string
  emoji?: string | null
  externalFileIcon?: string | null
  channel?: ChannelType | DesignChannel | null
  onEmojiSelect?: (emoji: BaseEmoji | null) => void
  onChange: (title: string) => void
  onClose?: () => void
  forceOpen?: boolean
  autoFocus?: boolean
  withChannels?: boolean
  isDisabled?: boolean
  assetType?: AssetType
  trigger?: ReactNode
  scheduledPostTargets?: ScheduledPostTarget[]
  brandPlan?: BrandPlan | null
}

interface PopoverProps extends Props {
  close: () => void
}

const Popover = ({
  close,
  emoji,
  title,
  onEmojiSelect,
  onChange,
  fileKey,
  autoFocus,
  withChannels,
  assetType,
}: PopoverProps) => {
  const ref = useCallback(
    (node: HTMLInputElement | null) => {
      if (node && autoFocus) {
        setTimeout(() => node.focus(), 10)
      }
    },
    [autoFocus],
  )

  return (
    <div className={Styles.wrapper}>
      {assetType !== 'Project' && onEmojiSelect && (
        <EmojiPicker
          size="md"
          emoji={emoji || null}
          onSelect={onEmojiSelect}
          fileKey={fileKey}
          withChannels={withChannels}
        />
      )}
      <div className={Styles.titleInput}>
        <Input
          ref={ref}
          autoFocus={autoFocus}
          aria-label="Title"
          name="title"
          placeholder="Untitled"
          onKeyPress={(event) => {
            if (event.key === 'Enter') close()
          }}
          onChange={(event) => onChange(event.currentTarget.value)}
          value={title === 'Untitled' ? '' : title}
          size="lg"
          className={Styles.input}
        />
      </div>
    </div>
  )
}

const AlmanacFileTitle = observer(
  ({
    title,
    emoji,
    externalFileIcon,
    onEmojiSelect,
    onChange,
    onClose,
    children,
    trigger,
    channel,
    fileKey,
    withChannels = false,
    autoFocus = false,
    forceOpen,
    size = 'md',
    isDisabled = false,
    assetType,
    scheduledPostTargets,
    brandPlan,
  }: PropsWithChildren<Props>) => {
    const theme = useHeaderTheme()
    const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
    const wixEnabled = useFlipperFlag('wix_blog_integration')
    const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
    const isScheduledPostsV3 = useIsScheduledPostsV3()
    const { connectedBlogIntegration } = useBlogIntegrations()

    const frontIcon = () => {
      if (isScheduledPostsV3 && scheduledPostTargets && scheduledPostTargets.length > 0) {
        return (
          <div className={Styles.platformIconsContainer}>
            {scheduledPostTargets.map((target, index) => {
              const channelType = channelTypeFromProvider[target.provider]
              if (!channelType) return null

              return (
                <div
                  key={target.id}
                  className={Styles.platformIcon}
                  style={{ zIndex: scheduledPostTargets.length - index }}
                >
                  <ChannelIcon
                    channel={channelType}
                    isWhite={theme === 'dark'}
                    blogPlatform={getBlogPlatformBrandingWithOnboardingSelection({
                      channel: channelType,
                      externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
                      selectedBlogPlatform: brandPlan?.selected_blog_platform,
                      featureFlags: {
                        goHighLevelEnabled,
                        wixEnabled,
                        blogPlatformOnboardingBrandingEnabled,
                      },
                    })}
                  />
                </div>
              )
            })}
          </div>
        )
      }

      return (
        <>
          {channel && !emoji && (
            <ChannelIcon
              channel={channel}
              isWhite={theme === 'dark'}
              blogPlatform={getBlogPlatformBrandingWithOnboardingSelection({
                channel,
                externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
                selectedBlogPlatform: brandPlan?.selected_blog_platform,
                featureFlags: {
                  goHighLevelEnabled,
                  wixEnabled,
                  blogPlatformOnboardingBrandingEnabled,
                },
              })}
            />
          )}
          {externalFileIcon && !emoji && externalFileEmoji({ external_file_icon: externalFileIcon })}
          {!channel && !externalFileIcon && !emoji && (
            <>{assetType === 'Design' ? <Design size={16} /> : <Document size={16} />}</>
          )}
          {emoji}
        </>
      )
    }

    const showPostPreview = channel && TYPES_WITH_PREVIEW.includes(channel)

    let canSelectCustomEmoji = true
    if (isScheduledPostsV3 && showPostPreview) {
      canSelectCustomEmoji = false
    }

    return (
      <DropdownTrigger
        restoreFocus={false}
        forceOpen={forceOpen}
        placement="bottom-start"
        containFocus={false}
        popoverClassName={Styles.popover}
        autoUpdateOptions={{
          animationFrame: false,
          elementResize: false,
        }}
        onOpenChange={(isOpen) => !isOpen && onClose?.()}
      >
        {trigger || (
          <Button
            variant="tertiary"
            size={size}
            className={Styles.titleButton}
            isDisabled={isDisabled}
            aria-label={title}
            frontIcon={children ? undefined : frontIcon}
          >
            {children || (
              <div className={Styles.titleContainer}>
                <span className={Styles.title}>{title}</span>
              </div>
            )}
          </Button>
        )}
        {(close) => (
          <Popover
            close={close}
            title={title}
            emoji={emoji}
            fileKey={fileKey}
            onEmojiSelect={canSelectCustomEmoji ? onEmojiSelect : undefined}
            onChange={onChange}
            autoFocus={autoFocus}
            withChannels={withChannels}
            assetType={assetType}
          />
        )}
      </DropdownTrigger>
    )
  },
)

const AlmanacFileTitleContainer = ({ title, children, ...rest }: PropsWithChildren<Props>) => (
  <ErrorBoundary fallback={<>{title}</>}>
    <AlmanacFileTitle title={title} {...rest}>
      {children}
    </AlmanacFileTitle>
  </ErrorBoundary>
)

export { AlmanacFileTitleContainer as AlmanacFileTitle }
